/**
 * Zerlegen, Zusammensetzen und Prüfen der Freitextfelder einer View (REQ-29 §2.2).
 *
 * Die drei Felder gehen unverändert an die API und werden erst beim Öffnen der View
 * ausgewertet — ein Tippfehler fällt also nicht beim Speichern auf, sondern später und
 * jemand anderem. Deshalb wird hier **gewarnt, nicht gesperrt**: „Save" bleibt bedienbar,
 * auch wenn diese Prüfung einen Ausdruck nicht versteht.
 */

import { parseFilterOperator } from '@/components/grid/defaultFilter';
import type { EntityTypes } from '@/types/enums';

import { findEntityField, hasEntityFields } from './entityFields';

/* ── Additional Functions ─────────────────────────────────────────────────── */

/**
 * Die Schlüsselwörter, die Cargoflow tatsächlich auswertet. Belegt mit
 * `grep -rn "AdditionalFunctions?.includes(" src/pages/views/`: `ContainerView.tsx`
 * (`Dokument`, `Frachtkosten`, `Duplizieren`), `OrderPositionView.tsx` (`Frachtkosten`,
 * `Aufteilen`) und `ReceiptView.tsx` (`Dokument`, `ReceiptAssignment`).
 * `ReceiptAssignmentView.tsx` wertet keines aus.
 */
export const ADDITIONAL_FUNCTIONS = [
  'Dokument',
  'Frachtkosten',
  'Duplizieren',
  'Aufteilen',
  'ReceiptAssignment',
] as const;

export type AdditionalFunction = (typeof ADDITIONAL_FUNCTIONS)[number];

/** Was das Schlüsselwort in der View bewirkt — der Wortlaut der Schaltflächen ist zitiert. */
export const ADDITIONAL_FUNCTION_HINTS: Readonly<Record<AdditionalFunction, string>> = {
  Dokument: 'Menü „Documents" in Container und Rechnung: Dokumente hochladen und ansehen.',
  Frachtkosten:
    'Schaltfläche „Calculate Freight Charges" in Container und Bestellposition: rechnet die Frachtkosten neu.',
  Duplizieren: 'Schaltfläche „Container duplizieren" in der Containeransicht.',
  Aufteilen: 'Schaltfläche „Divide Positions" in der Bestellpositionsansicht.',
  ReceiptAssignment: 'Schaltfläche „Assign to Container" in der Rechnungsansicht.',
};

export interface AdditionalFunctionsValue {
  active: readonly AdditionalFunction[];
  /** Was außer den Schlüsselwörtern im Feld steht. */
  rest: string;
}

/**
 * Ein Kästchen ist gesetzt, sobald das Schlüsselwort **irgendwo** im Text steht — genau die
 * Teilzeichenkettenprüfung, die die Ansichtsseiten machen. Für den Rest werden die
 * Vorkommen herausgeschnitten; was danach leer ist, fällt weg.
 */
export function parseAdditionalFunctions(raw: string | null | undefined): AdditionalFunctionsValue {
  const text = raw ?? '';
  const active = ADDITIONAL_FUNCTIONS.filter((keyword) => text.includes(keyword));

  const rest = text
    .split(/[;,]/)
    .map((part) =>
      ADDITIONAL_FUNCTIONS.reduce((carry, keyword) => carry.split(keyword).join(''), part)
        .replace(/\s+/g, ' ')
        .trim(),
    )
    .filter((part) => part !== '')
    .join(', ');

  return { active, rest };
}

/** Setzt den Freitext wieder zusammen. Leer ergibt `null` — die Spalte ist nullable. */
export function buildAdditionalFunctions(value: AdditionalFunctionsValue): string | null {
  const parts = [
    ...ADDITIONAL_FUNCTIONS.filter((keyword) => value.active.includes(keyword)),
    value.rest.trim(),
  ].filter((part) => part !== '');

  return parts.length === 0 ? null : parts.join(', ');
}

/* ── Default Sort Query ───────────────────────────────────────────────────── */

/**
 * Gegenstück zu `parseDefaultOrder`: Der Client trennt an `,`, dann an ` `, nimmt das erste
 * Wort als Feld und prüft beim zweiten auf „desc".
 */
export function checkDefaultOrder(raw: string | null | undefined, entity: EntityTypes): string[] {
  const text = (raw ?? '').trim();
  if (text === '') return [];

  const warnings: string[] = [];

  for (const entry of (raw ?? '').split(',')) {
    const trimmed = entry.trim();
    if (trimmed === '') {
      warnings.push('Ein Sortierkriterium ist leer und wird verworfen.');
      continue;
    }

    const tokens = trimmed.split(' ');
    if (tokens.length > 2) {
      warnings.push(
        `„${trimmed}" enthält mehr als Feldname und Richtung. Gelesen werden nur die ersten beiden Angaben — auch ein zweites Leerzeichen zählt dazu.`,
      );
    }

    const direction = tokens[1];
    if (direction !== undefined && direction !== '') {
      const lower = direction.toLowerCase();
      if (lower !== 'asc' && lower !== 'desc') {
        warnings.push(
          `„${direction}" ist keine Richtung. Erlaubt sind „asc" und „desc"; alles andere gilt als „asc".`,
        );
      }
    }

    warnings.push(...checkFieldName(tokens[0] ?? '', entity));
  }

  return unique(warnings);
}

/* ── Default Filter Query ─────────────────────────────────────────────────── */

/**
 * Gegenstück zu `parseDefaultFilter`: Der Client trennt an `||`, dann an `&&`, dann an ` `
 * und erwartet je Bedingung genau `Feld Operator Wert`.
 */
export function checkDefaultFilter(raw: string | null | undefined, entity: EntityTypes): string[] {
  const text = (raw ?? '').trim();
  if (text === '') return [];

  const warnings: string[] = [];

  for (const group of (raw ?? '').split('||')) {
    for (const expression of group.split('&&')) {
      const condition = expression.trim();
      if (condition === '') {
        warnings.push('Eine Bedingung ist leer und wird verworfen.');
        continue;
      }

      const tokens = condition.split(' ');
      const [field, operator, value] = tokens;
      if (field === undefined || field === '' || operator === undefined || value === undefined) {
        warnings.push(
          `Die Bedingung „${condition}" konnte nicht gelesen werden. Erwartet werden Feldname, Operator und Wert, mit je einem Leerzeichen getrennt.`,
        );
        continue;
      }

      if (parseFilterOperator(operator) !== operator.toLowerCase()) {
        warnings.push(`„${operator}" ist kein bekannter Operator und wird als „contains" gelesen.`);
      }

      if (tokens.length > 3) {
        warnings.push(
          `Der Wert in „${condition}" enthält ein Leerzeichen. Gelesen wird nur „${value}".`,
        );
      }

      if (value.includes('"')) {
        warnings.push(
          `Der Wert in „${condition}" enthält ein Anführungszeichen. Der Filter übernimmt es als Teil des Wertes.`,
        );
      }

      warnings.push(...checkFieldName(field, entity));
    }
  }

  return unique(warnings);
}

/* ── Gemeinsames ──────────────────────────────────────────────────────────── */

/** Ohne Feldliste zur Entität lässt sich über den Feldnamen nichts sagen. */
function checkFieldName(name: string, entity: EntityTypes): string[] {
  if (name === '' || !hasEntityFields(entity)) return [];
  if (findEntityField(entity, name) !== undefined) return [];
  return [`Das Feld „${name}" gibt es an dieser Entität nicht.`];
}

/** `AdminWarnings` nimmt den Text als Schlüssel — derselbe Befund darf nur einmal kommen. */
function unique(warnings: readonly string[]): string[] {
  return [...new Set(warnings)];
}
